/* eslint-disable */
import { useEffect, useState, useRef } from "react";
import { FiX, FiCopy, FiDownload, FiPrinter, FiRefreshCw, FiShield, FiCheckCircle, FiAlertTriangle } from "react-icons/fi";
import { Spinner } from "components/ui";
import { BackupCodeGrid, MfaIconBadge, mfaActionButtonClass, otpInputClass } from "./MfaUi";
import { toast } from "react-toastify";
import { useGenerateMfa, useVerifyMfa, useRegenerateBackupCodes } from "./useMfa";
import { useAuth } from "context/auth-context";

type Props = {
  onClose: () => void;
};

const MfaSetupPage = ({ onClose }: Props) => {
  const [qrCode, setQrCode] = useState<string>("");
  const [secret, setSecret] = useState<string>("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const [step, setStep] = useState<number>(1);
  const { auth } = useAuth();

  // 🔥 React Query hooks
  const { mutateAsync: generateMfa, isLoading: loadingGenerate } = useGenerateMfa();
  const { mutateAsync: verifyMfa, isLoading: loadingVerify } = useVerifyMfa();
  const { mutateAsync: regenerateCodes, isLoading: loadingRegen } = useRegenerateBackupCodes();

  const isQrLoading = loadingGenerate;
  const isVerifyLoading = loadingVerify;
  const isRegenLoading = loadingRegen;

  const initMFA = async () => {
    try {
      if (!auth?.email) return;

      const res = await generateMfa(auth.email);

      setQrCode(res?.data?.data?.qrCode || "");
      setSecret(res?.data?.data?.secret || "");
    } catch (err) {
      console.error("MFA INIT ERROR:", err);
      toast.error("Failed to initialize MFA");
    }
  };

  useEffect(() => {
    if (!auth?.email) return;
    initMFA();
  }, [auth?.email]);

  const verifyOtp = async () => {
    const finalOtp = otp.join("");

    if (finalOtp.length !== 6) {
      return toast.error("Enter valid 6-digit OTP");
    }

    try {
      const res = await verifyMfa({ email: auth?.email, token: finalOtp });
      if (res?.data?.success) {
        setBackupCodes(res?.data?.data?.backupCodes || []);
        toast.success(res?.data?.message || "MFA enabled successfully");
        setStep(3);
      } else {
        toast.error(res?.data?.message || "Invalid OTP");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Invalid OTP");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Copied");
  };

  const handleDownload = () => {
    const blob = new Blob([backupCodes.join("\n")], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "mfa-backup-codes.txt";
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const win = window.open("", "", "width=600,height=600");
    win?.document.write(`<pre>${backupCodes.join("\n")}</pre>`);
    win?.document.close();
    win?.print();
  };

  const handleRegenerate = async () => {
    try {
      const res = await regenerateCodes(auth?.email);
      setBackupCodes(res?.data?.data?.backupCodes || []);
      toast.success("Backup codes regenerated");
    } catch {
      toast.error("Failed to regenerate");
    }
  };

  // OTP handlers
  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    const newOtp = [...otp];

    if (e.key === "Backspace") {
      if (otp[index]) {
        newOtp[index] = "";
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
        newOtp[index - 1] = "";
      }
      setOtp(newOtp);
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }

    if (e.key === "ArrowRight" && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "");

    if (paste.length === 6) {
      setOtp(paste.split(""));
      inputsRef.current[5]?.focus();
    }
  };

  const steps = ["Scan", "Verify", "Backup codes", "Done"];

  return (
    <div className="zs-backdrop" role="dialog" aria-modal="true" aria-labelledby="mfa-setup-title">
      <div className="zs-modal max-w-md">
        <button onClick={onClose} className="zs-icon-btn zs-modal-close" aria-label="Close">
          <FiX />
        </button>

        <div className="px-5 pt-5 pb-4 border-b sm:px-6 border-line">
          <div className="flex items-start gap-3 pr-8">
            <MfaIconBadge>
              <FiShield />
            </MfaIconBadge>
            <div className="flex flex-col gap-1 min-w-0">
              <h2 id="mfa-setup-title" className="text-base font-semibold font-display text-ink">
                Set up Multi-Factor Authentication
              </h2>
              <p className="text-sm text-ink-3">Protect your account with an extra layer of security</p>
            </div>
          </div>

          <ol className="grid grid-cols-4 gap-2 mt-5" aria-label="Setup progress">
            {steps.map((label, index) => {
              const position = index + 1;
              const isDone = step > position;
              const isCurrent = step === position;
              return (
                <li key={label} className="flex flex-col gap-1.5 min-w-0" aria-current={isCurrent ? "step" : undefined}>
                  <span
                    className={`h-1 rounded-full ${isDone || isCurrent ? "bg-primary" : "bg-surface-hover"}`}
                  />
                  <span
                    className={`text-[11px] font-medium truncate ${
                      isCurrent ? "text-primary-text" : isDone ? "text-ink-2" : "text-ink-4"
                    }`}
                  >
                    {position}. {label}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="px-5 py-5 sm:px-6">
          {!qrCode ? (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-sm text-ink-3">
              <Spinner className="w-6 h-6" />
              Generating QR...
            </div>
          ) : (
            <>
              {step === 1 && (
                <div className="flex flex-col gap-4 text-center">
                  <p className="text-sm text-ink-2">Scan this QR code using Microsoft Authenticator or any OTP app</p>
                  <div className="flex flex-col items-center gap-3 p-4 border rounded-lg bg-surface-2 border-line">
                    {qrCode && (
                      <span className="p-2 zs-logo-plate">
                        <img src={qrCode} className="w-40 h-40" />
                      </span>
                    )}
                    <div className="w-full">
                      <p className="text-[11px] font-semibold tracking-wide uppercase text-ink-4">Setup key</p>
                      <p className="mt-1 font-mono text-xs break-all text-ink-2">{secret}</p>
                    </div>
                  </div>

                  <button onClick={() => setStep(2)} className="zs-btn zs-btn-primary zs-btn-block">
                    Continue
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="flex flex-col items-center gap-5 text-center">
                  <p className="text-sm text-ink-2">Enter the 6-digit code from your authenticator app</p>
                  <div className="flex justify-center w-full gap-2">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        value={digit}
                        maxLength={1}
                        inputMode="numeric"
                        aria-label={`Digit ${index + 1}`}
                        ref={(el) => (inputsRef.current[index] = el)}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={handlePaste}
                        className={otpInputClass}
                      />
                    ))}
                  </div>

                  <button
                    onClick={verifyOtp}
                    disabled={isVerifyLoading}
                    className="zs-btn zs-btn-primary zs-btn-block"
                  >
                    {isVerifyLoading ? "Verifying..." : "Verify"}
                  </button>
                </div>
              )}

              {step === 3 && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <h3 className="text-sm font-semibold text-ink">Save Your Backup Codes</h3>
                    <p className="text-sm text-ink-3">Store these codes safely. Each code can be used once.</p>
                  </div>
                  <BackupCodeGrid codes={backupCodes} />

                  <div className="grid grid-cols-2 gap-2">
                    {/* COPY */}
                    <button onClick={handleCopy} className={mfaActionButtonClass}>
                      <FiCopy size={14} />
                      Copy
                    </button>

                    {/* DOWNLOAD */}
                    <button onClick={handleDownload} className={mfaActionButtonClass}>
                      <FiDownload size={14} />
                      Download
                    </button>

                    {/* PRINT */}
                    <button onClick={handlePrint} className={mfaActionButtonClass}>
                      <FiPrinter size={14} />
                      Print
                    </button>

                    {/* REGENERATE */}
                    <button onClick={handleRegenerate} className={`${mfaActionButtonClass} text-danger`}>
                      <FiRefreshCw size={14} />
                      Regenerate
                    </button>
                  </div>

                  {/* NOTE TEXT */}
                  <p className="flex items-center justify-center gap-1.5 text-xs font-medium text-warning">
                    <FiAlertTriangle className="w-3.5 h-3.5" />
                    Each backup code works only once
                  </p>

                  <button onClick={() => setStep(4)} className="zs-btn zs-btn-primary zs-btn-block">
                    Continue
                  </button>
                </div>
              )}

              {step === 4 && (
                <div className="flex flex-col items-center gap-3 py-4 text-center">
                  {/* ICON */}
                  <MfaIconBadge tone="success" size="lg">
                    <FiCheckCircle />
                  </MfaIconBadge>

                  {/* TITLE */}
                  <h3 className="text-lg font-semibold font-display text-ink">MFA Enabled Successfully</h3>

                  {/* SUBTEXT */}
                  <p className="max-w-xs text-sm text-ink-3">
                    Your account is now protected with multi-factor authentication.
                  </p>

                  {/* BUTTON */}
                  <button onClick={onClose} className="mt-2 zs-btn zs-btn-primary zs-btn-block">
                    Done
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MfaSetupPage;
